import { useMemo, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import FilterListIcon from '@mui/icons-material/FilterList'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined'
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FaceRetouchingNaturalOutlinedIcon from '@mui/icons-material/FaceRetouchingNaturalOutlined'
import { useTranslation } from 'react-i18next'
import SectionHeader from '@/components/researcher/SectionHeader'
import type { DeviceKind } from './monitoringMockData'

export type RespondentStatus = 'invited' | 'progress' | 'done' | 'drop'

export interface RespondentRow {
  /** Display label: the invited respondent's email, or "16 min temu" for
   *  anonymous walk-ins (no more R-#id). */
  id: string
  /** Real ProjectParticipant id — used to drill into this respondent's emotions. */
  respondentId: number
  /** Face-preview avatar captured at record time; null → fall back to an icon. */
  thumbnailUrl?: string | null
  status: RespondentStatus
  started: string
  duration: string
  device: DeviceKind
  age: string
  city: string
  /** How the respondent entered: an emailed invite (tracked) or the public link. */
  source?: 'invited' | 'anonymous'
}

type FilterKey = 'all' | 'done' | 'progress' | 'drop' | 'invited'

type RespondentsTableProps = {
  rows: RespondentRow[]
  invitedCount: number
  counts: Record<FilterKey, number>
  onInvite?: () => void
  /** Opens the emotion analysis for a single respondent. */
  onSelectRespondent?: (respondentId: number) => void
  /** Removes one respondent and everything recorded about them (Z-48). */
  onDeleteRespondent?: (row: RespondentRow) => void
}

function DeviceTag({ kind }: { kind: DeviceKind }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('monitoring')
  if (kind === 'none') return <Box component="span" sx={{ color: c.ink32 }}>—</Box>
  const Icon = kind === 'mobile' ? SmartphoneOutlinedIcon : DesktopWindowsOutlinedIcon
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: c.ink72 }}>
      <Icon sx={{ fontSize: 14 }} />
      <Box component="span" sx={{ textTransform: 'capitalize' }}>
        {t(`respondents.device.${kind}`)}
      </Box>
    </Box>
  )
}

/** Live respondents list with status filter chips and anonymized IDs. */
export default function RespondentsTable({
  rows,
  invitedCount,
  counts,
  onInvite,
  onSelectRespondent,
  onDeleteRespondent,
}: RespondentsTableProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('monitoring')
  const [filter, setFilter] = useState<FilterKey>('all')
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null)

  const statusMeta: Record<RespondentStatus, { dot: string; fg: string }> = {
    invited: { dot: c.ink32, fg: c.ink56 },
    progress: { dot: c.warning, fg: '#B0701F' },
    done: { dot: c.success, fg: '#3D8077' },
    drop: { dot: c.danger, fg: '#A12F2A' },
  }

  const visibleRows = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  )

  const chips: FilterKey[] = ['all', 'done', 'progress', 'drop', 'invited']

  const headCellSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: c.ink48,
    borderBottom: `1px solid ${c.ink08}`,
    py: 1,
  }
  const bodyCellSx = {
    fontSize: 13,
    color: c.ink,
    borderBottom: `1px solid ${c.ink08}`,
    py: 1.5,
  }

  const ghostBtnSx = {
    color: c.ink72,
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    '&:hover': { bgcolor: c.gray50 },
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 1.75, md: 2.5 }, borderRadius: '18px', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.75 }}
    >
      <SectionHeader
        title={t('respondents.title')}
        subtitle={t('respondents.subtitle', { count: invitedCount })}
        right={
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button
              size="small"
              startIcon={<FilterListIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              sx={ghostBtnSx}
            >
              {filter === 'all' ? t('respondents.filter') : t(`respondents.chips.${filter}`)}
            </Button>
            <Menu
              anchorEl={filterAnchor}
              open={Boolean(filterAnchor)}
              onClose={() => setFilterAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {chips.map((f) => (
                <MenuItem
                  key={f}
                  selected={filter === f}
                  onClick={() => {
                    setFilter(f)
                    setFilterAnchor(null)
                  }}
                  sx={{ gap: 1.25, fontSize: 13, minWidth: 200 }}
                >
                  <Box sx={{ width: 18, display: 'inline-flex', justifyContent: 'center' }}>
                    {filter === f && <CheckRoundedIcon sx={{ fontSize: 16, color: c.coral }} />}
                  </Box>
                  <Box component="span" sx={{ flex: 1 }}>{t(`respondents.chips.${f}`)}</Box>
                  <Box
                    component="span"
                    sx={{
                      px: 0.75,
                      borderRadius: 34,
                      bgcolor: c.gray100,
                      color: c.ink56,
                      fontSize: 11,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {counts[f]}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
            {onInvite && (
              <Button
                size="small"
                variant="contained"
                onClick={onInvite}
                startIcon={<PersonAddAlt1OutlinedIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                {t('respondents.invite')}
              </Button>
            )}
          </Stack>
        }
      />

      <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 640, '& td, & th': { px: 0, pr: 1.75 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>{t('respondents.columns.id')}</TableCell>
            <TableCell sx={headCellSx}>{t('respondents.columns.status')}</TableCell>
            <TableCell sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{t('respondents.columns.started')}</TableCell>
            <TableCell sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{t('respondents.columns.duration')}</TableCell>
            <TableCell sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{t('respondents.columns.device')}</TableCell>
            <TableCell sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{t('respondents.columns.demographics')}</TableCell>
            <TableCell sx={headCellSx} align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((r) => {
            const sm = statusMeta[r.status]
            return (
              <TableRow
                key={r.respondentId}
                hover
                onClick={() => onSelectRespondent?.(r.respondentId)}
                sx={{ cursor: onSelectRespondent ? 'pointer' : 'default' }}
              >
                <TableCell sx={{ ...bodyCellSx, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={r.thumbnailUrl || undefined}
                      sx={{ width: 30, height: 30, bgcolor: c.gray100, color: c.ink48, flexShrink: 0 }}
                    >
                      <FaceRetouchingNaturalOutlinedIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    {r.source === 'invited' && (
                      <MailOutlineIcon sx={{ fontSize: 14, color: c.coral }} titleAccess={t('respondents.invitedSource')} />
                    )}
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: 110, sm: 200 }, whiteSpace: 'nowrap' }}>
                      {r.id}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: sm.dot }} />
                    <Box component="span" sx={{ color: sm.fg, fontWeight: 500 }}>
                      {t(`status.${r.status}`)}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, color: c.ink72, fontVariantNumeric: 'tabular-nums', display: { xs: 'none', sm: 'table-cell' } }}>
                  {r.started}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, color: c.ink72, fontVariantNumeric: 'tabular-nums', display: { xs: 'none', sm: 'table-cell' } }}>
                  {r.duration}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', md: 'table-cell' } }}>
                  <DeviceTag kind={r.device} />
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, color: c.ink72, display: { xs: 'none', md: 'table-cell' } }}>
                  {[r.age, r.city].filter(Boolean).length ? (
                    [r.age, r.city].filter(Boolean).join(' · ')
                  ) : (
                    <Box component="span" sx={{ color: c.ink32 }}>—</Box>
                  )}
                </TableCell>
                <TableCell sx={bodyCellSx} align="right">
                  <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                    {onDeleteRespondent && (
                      <IconButton
                        size="small"
                        aria-label={t('respondents.delete')}
                        title={t('respondents.delete')}
                        sx={{ color: c.ink48, '&:hover': { color: c.danger } }}
                        onClick={(e) => {
                          e.stopPropagation()
                          onDeleteRespondent(r)
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                    <IconButton size="small" aria-label={t('respondents.details')} sx={{ color: c.ink48 }}>
                      <ChevronRightIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      </TableContainer>
    </Paper>
  )
}
